import { supabase } from '@/utils/supabase';
import {
  getAttachmentById,
  getAttachmentUrl,
  getEncompassDocuments,
  resolveAttachmentFilename
} from './encompassDocumentService';
import type { EncompassAttachment, EncompassDocumentWithAttachments } from '@/types/encompass-documents';

// Match the durable BSA upload limit. Bound the browser's retained batch too.
export const EFOLDER_PDF_LIMIT = 25 * 1024 * 1024;
export const EFOLDER_BATCH_LIMIT = 100 * 1024 * 1024;

export interface AnalyzerEfolder {
  loanId: string;
  encompassLoanId: string;
  borrower: string;
  loanNumber: string;
  documents: EncompassDocumentWithAttachments[];
}

/** Resolve the selected BSA borrower through the caller's RLS session, never by name. */
export async function loadAnalyzerEfolder(loanId: string, signal: AbortSignal): Promise<AnalyzerEfolder> {
  signal.throwIfAborted();
  const { data: loan, error } = await supabase.from('encompass_loans')
    .select('id,encompass_loan_id,borrower_first_name,borrower_last_name,trans_details_loan_number')
    .eq('id', loanId).abortSignal(signal).maybeSingle();
  signal.throwIfAborted();
  if (error || !loan || loan.id !== loanId || !loan.encompass_loan_id) {
    throw new Error('This loan is no longer available to your account. Reopen it from Loan Center.');
  }
  const documents = await getEncompassDocuments(loan.encompass_loan_id);
  signal.throwIfAborted();
  return {
    loanId,
    encompassLoanId: loan.encompass_loan_id,
    borrower: [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean).join(' '),
    loanNumber: loan.trans_details_loan_number || '—',
    // Same current-version default as Loan Center; no extracted borrower text is read.
    documents: documents.filter(doc => doc.encompass_loan_id === loan.encompass_loan_id)
      .map(doc => ({ ...doc, attachments: doc.attachments.filter(file =>
        file.encompass_loan_id === loan.encompass_loan_id && file.is_active !== false
      ) }))
  };
}

export function efolderUnavailableReason(file: EncompassAttachment): string | null {
  if (file.is_active === false || file.download_status === 'deleted') {
    return 'This version is no longer current.';
  }
  if (file.download_status !== 'downloaded' || !file.storage_path) {
    return 'File is not available yet. Check its sync status in Loan Center.';
  }
  if (file.storage_bucket !== 'loan-documents') {
    return 'This file is not available from the loan document store.';
  }
  if (!resolveAttachmentFilename(file).toLowerCase().endsWith('.pdf')) {
    return 'Only PDF statements can be added.';
  }
  if (file.file_size && file.file_size > EFOLDER_PDF_LIMIT) {
    return 'This file exceeds the 25 MB statement limit.';
  }
  return null;
}

async function readPdf(response: Response, signal: AbortSignal, budget: number): Promise<Blob> {
  if (!response.ok || !response.body) {
    throw new Error('The file could not be downloaded. Try again.');
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  const limit = Math.min(EFOLDER_PDF_LIMIT, budget);
  try {
    if (Number(response.headers.get('content-length')) > limit) {
      throw new Error('Selection exceeds the 25 MB per file or 100 MB per import limit.');
    }
    while (true) {
      signal.throwIfAborted();
      const { done, value } = await reader.read();
      signal.throwIfAborted();
      if (done) {
        break;
      }
      size += value.byteLength;
      if (size > limit) {
        throw new Error('Selection exceeds the 25 MB per file or 100 MB per import limit.');
      }
      chunks.push(new Uint8Array(value));
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
  const blob = new Blob(chunks, { type: 'application/pdf' });
  if (!(await blob.slice(0, 1024).text()).includes('%PDF-')) {
    throw new Error('The selected file is not a valid PDF. Check it in Loan Center.');
  }
  return blob;
}

/** Read selected bytes only, sequentially. No eFolder writes, resyncs, or eager downloads. */
export async function downloadAnalyzerEfolderFiles(
  folder: AnalyzerEfolder,
  selectedIds: string[],
  maxFiles: number,
  signal: AbortSignal
): Promise<File[]> {
  signal.throwIfAborted();
  const ids = [...new Set(selectedIds)];
  if (!Number.isInteger(maxFiles) || maxFiles < 1 || maxFiles > 24 || !ids.length || ids.length > maxFiles) {
    throw new Error(`Select between 1 and ${maxFiles} statements.`);
  }
  const available = new Set(folder.documents.flatMap(doc => doc.attachments.map(file => file.id)));
  if (ids.some(id => !available.has(id))) {
    throw new Error('The document selection changed. Reopen eFolder documents.');
  }
  const files: File[] = [];
  let totalSize = 0;
  for (const id of ids) {
    signal.throwIfAborted();
    // Recheck the authoritative row with the caller's session immediately before signing.
    const attachment = await getAttachmentById(id);
    signal.throwIfAborted();
    if (!attachment || attachment.id !== id || attachment.encompass_loan_id !== folder.encompassLoanId) {
      throw new Error('A selected document is no longer available on this loan. Reopen eFolder documents.');
    }
    const reason = efolderUnavailableReason(attachment);
    if (reason) {
      throw new Error(reason);
    }
    const url = await getAttachmentUrl(attachment.storage_path!);
    signal.throwIfAborted();
    if (!url) {
      throw new Error('The selected file is no longer available. Reopen eFolder documents.');
    }
    const response = await fetch(url, { signal, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer' });
    const blob = await readPdf(response, signal, EFOLDER_BATCH_LIMIT - totalSize);
    signal.throwIfAborted();
    totalSize += blob.size;
    files.push(new File([blob], resolveAttachmentFilename(attachment), { type: 'application/pdf' }));
  }
  return files;
}
